// src/utils/gunAuth.ts
import SEA from './sea';

// Relative sibling imports, deliberately: this folder is meant to be lifted
// into other projects wholesale, and an app-alias import is the one thing that
// breaks the moment the folder lands in a repo without that exact alias.
import { gunUser, gunInstance, ownedRootWritable, devicesNode, setLastAlias, getLastAlias, waitForPeer, gunRecallSession, looksLikePub, onSessionReset, runSessionReset } from './gunClient';
import { gunPut } from './gunWrite';
import { localStore as storage } from './support/localStore';
import { GUN_STORAGE_KEYS as STORAGE_KEYS, GUN_STORAGE_PREFIX } from './gunConfig';

// Verbose auth logging. Env-driven like gunClient's DEBUG_GUN — "edit the
// source to turn on logging" is exactly what a lifted layer must not require.
// Off by default even in dev: the auth transcript names aliases and storage
// keys, and it is loud.
const DEBUG_AUTH = String(import.meta.env?.VITE_GUN_DEBUG_AUTH ?? '') === '1';

function debugLog(...args: any[]) {
  if (DEBUG_AUTH) {
    console.log('[gunAuth]', ...args);
  }
}

type Fail = { ok: false; error: string };

/** Upper bound on how long we wait for Gun's `user.auth` ack before failing. */
const AUTH_ACK_TIMEOUT_MS = 15000;

/**
 * True only while one of our own auth calls is genuinely outstanding.
 *
 * SEA guards `user.auth`/`user.create` with an in-flight latch (`user._.ing`)
 * that it sets on entry and clears only when its own flow reaches success or
 * error. A relay that accepts the socket but never answers leaves the latch set
 * forever, so every later attempt is refused with "User is already being
 * created or authenticated!" until the page is reloaded — including the
 * re-auth that restores the session we just tore down.
 *
 * Tracking our own attempts lets us tell a genuinely concurrent call (which
 * must still be refused) from a latch stranded by an earlier timeout (which
 * must be cleared).
 */
let authInFlight = false;

/**
 * Clear a stranded SEA in-flight latch. No-op while one of our auth calls is
 * really running, so a concurrent attempt is still correctly refused.
 */
function clearStaleAuthLatch(u: any): void {
  if (authInFlight) return;
  try {
    if (u?._?.ing) {
      debugLog('[gunAuth] clearing stale SEA in-flight latch');
      u._.ing = false;
    }
  } catch {
    /* the latch is a best-effort recovery; never let it break auth */
  }
}

/**
 * Runs after a successful authentication, before the login resolves.
 *
 * The app registers whatever must happen between "this pub is authenticated"
 * and "the app may start syncing it" — here, wiping the previous account's
 * local data on an account switch. It is a registered hook rather than a direct
 * call because that wipe is app POLICY: this module used to import
 * `@shared/utils/userIsolation` outright, which pointed the dependency from the
 * Gun layer up into the app and is precisely what stops the layer being lifted
 * into another project unchanged.
 *
 * Awaited, and a rejection fails the login: whatever the app needs done before
 * syncing starts, it needs done.
 */
export type PostAuthHook = (pub: string) => Promise<void> | void;
let postAuthHook: PostAuthHook | null = null;

/** Register the {@link PostAuthHook}. Pass null to clear it. */
export function setPostAuthHook(fn: PostAuthHook | null): void {
  postAuthHook = fn;
}

async function runPostAuthHook(pub: string): Promise<void> {
  if (!postAuthHook) return;
  await postAuthHook(pub);
}

/**
 * Runs after a login fully succeeds, with the pub and alias in hand.
 *
 * This is where the app publishes whatever it wants the world to find about a
 * freshly authenticated account — the time tracker registers the alias in its
 * public search index here. A registered hook rather than an inline write for
 * the same reason as {@link PostAuthHook}: which public nodes exist is app
 * SCHEMA, and a login flow that writes `<root>/users/<alias>` would create that
 * index in every project this layer is lifted into.
 *
 * Fire-and-forget: publish failures must not fail a login that succeeded.
 */
export type LoginPublishHook = (pub: string, alias: string) => Promise<void> | void;
let loginPublishHook: LoginPublishHook | null = null;

/** Register the {@link LoginPublishHook}. Pass null to clear it. */
export function setLoginPublishHook(fn: LoginPublishHook | null): void {
  loginPublishHook = fn;
}

/**
 * Names this device for the account's device list.
 *
 * The layer records the FACTS about a device — its id, its user-agent string,
 * when it was last seen. What to call it is presentation: the mapping from a UA
 * string to "iPhone" or "Windows PC" is a list of product-copy strings, in one
 * language, reflecting one app's idea of which platforms are worth naming. That
 * list lived here, so every project lifting this folder inherited it.
 *
 * Unset, the device is recorded with its `ua` and no name, and the consumer
 * renders whatever it likes from that — nothing is lost, because the UA is the
 * actual evidence and the name never was.
 */
export type DeviceNameResolver = (ua: string) => string;
let deviceNameResolver: DeviceNameResolver | null = null;

/** Register the {@link DeviceNameResolver}. Pass null to clear it. */
export function setDeviceNameResolver(fn: DeviceNameResolver | null): void {
  deviceNameResolver = fn;
}

/**
 * Whether logging in should record this device on the relay.
 *
 * Default `true`, which is the behaviour the layer was lifted with — the origin
 * project shows a "your devices" list and needs it.
 *
 * An app can turn it off, and one does. `registerDevice` writes `{id, name, ua,
 * lastSeen}` to a public node: a user-agent string and a timestamp for every
 * device, which together are a per-device fingerprint and a record of when
 * somebody was online. For an app whose threat model treats pattern-of-use as
 * the sensitive part, that is the single most damaging thing it could leave on
 * a relay, and it was being written by simply calling `gunLogin`.
 *
 * A flag rather than a deletion so this file stays diffable against the project
 * it was lifted from — see `gun-layer-stays-liftable`.
 */
let deviceRegistrationEnabled = true;

/** Turn relay-side device registration on or off. See {@link deviceRegistrationEnabled}. */
export function setDeviceRegistrationEnabled(enabled: boolean): void {
  deviceRegistrationEnabled = enabled;
}

/** Exposed so logout and hard reset can unstick a stranded latch too. */
export function resetAuthInFlight(): void {
  authInFlight = false;
  try {
    const u: any = gunUser();
    if (u?._?.ing) u._.ing = false;
  } catch {
    /* ignore */
  }
}

/**
 * Stable per-device id.
 *
 * CSPRNG, not `Math.random()`: this id is one of the two inputs to the at-rest
 * key for the stored SEA pair (see derivePairKey), and it also namespaces the
 * guest timer soul in the public graph. `secureRandomId` itself lives under
 * `features/`, which `shared/` may not import, so the generation is inline.
 */
export function getOrCreateDeviceId() {
  let id = storage.get<string>(STORAGE_KEYS.DEVICE_ID, "");
  if (!id) {
    const bytes = new Uint8Array(16);
    (globalThis.crypto ?? ({} as Crypto)).getRandomValues?.(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    id = "dev_" + (hex || Math.random().toString(36).slice(2, 11));
    storage.set(STORAGE_KEYS.DEVICE_ID, id);
  }
  return id;
}

/**
 * Whether a pub is a signed-out guest identity (a device id) rather than a real
 * SEA account pub.
 */
export function isGuestPub(pub: string | null | undefined): boolean {
  return !pub || pub.startsWith("dev_");
}

export function registerDevice(pub: string) {
  // Checked first, before any storage read or write, so a disabled app leaves no
  // trace of the mechanism at all — not on the relay and not in local storage.
  if (!deviceRegistrationEnabled) return;

  const u = gunUser();
  if (!u.is || u.is.pub !== pub) return;

  const deviceId = getOrCreateDeviceId();

   // Throttle registration to once per hour to prevent sync storms.
   // The key includes the pub: it is per-account state, and the old
   // device-only key survived an account switch, so the next user's first
   // login silently skipped registering their device for up to an hour.
   // Under the layer's prefix so resetLocalAuthAndCache sweeps it.
   const lastSeenKey = `${GUN_STORAGE_PREFIX}_last_reg_${pub}_${deviceId}`;
   const now = Date.now();
   const lastReg = parseInt(storage.get<string>(lastSeenKey, "0"));
   if (now - lastReg < 1000 * 60 * 60) return;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  // The name is the app's to decide — see DeviceNameResolver. `undefined` is
  // dropped by prepareGunWrite, so an unnamed device stores just its facts.
  let name: string | undefined;
  try {
    name = deviceNameResolver?.(ua) || undefined;
  } catch {
    name = undefined;
  }

   const info = {
    id: deviceId,
    name,
    ua,
    lastSeen: new Date().toISOString()
  };

   storage.set(lastSeenKey, now.toString());
   // Under the app root, like every other app record. Writing app data at the
   // SEA user root is exactly what ownedRoot's namespacing exists to prevent —
   // that root also holds Gun's own account keys (pub, epub, alias, auth).
   gunPut(devicesNode(pub).get(deviceId), info, { label: `device ${deviceId}` });
}

// checked = remember across reloads/browser restarts (localStorage)
export function getRememberMe(): boolean {
  const v = storage.get<string>(STORAGE_KEYS.REMEMBER_ME, "1");
  return v === "1";
}

export function setRememberMe(v: boolean) {
  storage.set(STORAGE_KEYS.REMEMBER_ME, v ? "1" : "0");
}

// ------------------------------
// SEA pair at-rest encryption
// ------------------------------
// The pair (priv/epriv) is the account. Storing it as plaintext JSON in
// localStorage hands the whole account to any script that can read the origin's
// storage (e.g. the third-party ad script on this page). We encrypt it with a
// key derived from the device id + a random per-device salt via SEA.work.
//
// This is obfuscation-grade, not a vault: an attacker who can read localStorage
// can also read the salt and device id. It removes trivially harvestable
// plaintext keys and limits copy-paste exfiltration to this device.

const PAIR_ENVELOPE_VERSION = 1;

type PairEnvelope = { v: number; enc: string };

function isPairEnvelope(x: any): x is PairEnvelope {
  return !!x && typeof x === 'object' && x.v === PAIR_ENVELOPE_VERSION && typeof x.enc === 'string';
}

function getOrCreatePairSalt(): string {
  let salt = storage.get<string>(STORAGE_KEYS.PAIR_SALT, "");
  if (!salt) {
    const bytes = new Uint8Array(16);
    (globalThis.crypto ?? ({} as Crypto)).getRandomValues?.(bytes);
    salt = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    if (!salt) salt = Math.random().toString(36).slice(2) + Date.now().toString(36);
    storage.set(STORAGE_KEYS.PAIR_SALT, salt);
  }
  return salt;
}

async function derivePairKey(): Promise<string> {
  const key = await SEA.work(getOrCreateDeviceId(), getOrCreatePairSalt() as any);
  if (!key) throw new Error("failed to derive device key");
  return String(key);
}

function looksLikePair(x: any): boolean {
  return !!x && typeof x === 'object' && typeof x.pub === 'string' && typeof x.priv === 'string';
}

/** Encrypt and persist the SEA pair. Never falls back to plaintext. */
export async function writeStoredGunPair(pair: any): Promise<boolean> {
  if (!looksLikePair(pair)) return false;
  try {
    const enc = await SEA.encrypt(pair, await derivePairKey());
    if (!enc) throw new Error("encrypt returned empty");
    storage.set(STORAGE_KEYS.GUN_PAIR, { v: PAIR_ENVELOPE_VERSION, enc: String(enc) });
    return true;
  } catch (e) {
    console.warn("[gunAuth] failed to encrypt SEA pair; not persisting it:", e);
    storage.remove(STORAGE_KEYS.GUN_PAIR);
    return false;
  }
}

/**
 * Read the stored SEA pair. Encrypted envelope only.
 *
 * A plaintext entry (raw object or JSON string) used to be read and silently
 * upgraded. Accepting that shape meant anything able to write localStorage
 * could plant a pair the app would then authenticate as — and the upgrade path
 * kept the shape alive indefinitely. Anything that is not a well-formed
 * envelope is now simply not a session.
 */
async function readStoredGunPair(): Promise<any | null> {
  const stored = storage.get<any>(STORAGE_KEYS.GUN_PAIR, null);
  if (!stored) return null;
  if (!isPairEnvelope(stored)) {
    console.warn("[gunAuth] stored SEA pair is not an encrypted envelope; ignoring it");
    storage.remove(STORAGE_KEYS.GUN_PAIR);
    return null;
  }
  try {
    const pair = await SEA.decrypt(stored.enc, await derivePairKey());
    return looksLikePair(pair) ? pair : null;
  } catch (e) {
    console.warn("[gunAuth] failed to decrypt stored SEA pair:", e);
    return null;
  }
}

/**
 * A put whose ack is awaited — and bounded. Gun does not guarantee the callback
 * runs at all, so an unbounded version is an `await` that can hang for the rest
 * of the session; these run inside the login flow, where that would strand the
 * caller after authentication had already succeeded.
 *
 * Raw `.put`, and NOT {@link gunPutWithTimeout}, for one reason: the alias
 * republish below writes a Gun REFERENCE (`{ '~pub': { '#': '~pub' } }`), and
 * `prepareGunWrite` strips the keys `_`, `#` and `>` as Gun internals. Routed
 * through the guarded helpers the ref would arrive as an empty object and
 * `~@alias` would resolve to nothing — so this one write has to bypass the
 * cleaner. It still bounds and inspects its ack, which is what the helpers are
 * otherwise for.
 */
function putAck(node: any, data: any, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };
    const timer = setTimeout(
      () => settle(() => reject(new Error(`put ack timed out after ${timeoutMs}ms`))),
      timeoutMs,
    );
    try {
      // eslint-disable-next-line no-restricted-syntax -- a Gun ref cannot survive prepareGunWrite; ack handled here
      node.put(data, (ack: any) => {
        if (ack?.err) {
          settle(() => reject(new Error(String(ack.err))));
          return;
        }
        settle(resolve);
      });
    } catch (e) {
      settle(() => reject(e));
    }
  });
}

async function waitForAuthenticatedPub(timeoutMs = 3000, pollMs = 80): Promise<string | null> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const pub = gunUser()?.is?.pub ? String(gunUser().is.pub) : null;
    if (pub) return pub;
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
  return null;
}

/**
 * Force-publish the authenticated user's account graph to the relay so that
 * a fresh device can resolve ~@alias and authenticate. Best-effort; never throws.
 */
async function republishAccountToRelay(pub: string, alias: string) {
  const u = gunUser();
  if (!u?.is?.pub || u.is.pub !== pub) return;
  const cleanAlias = String(alias || "").trim();

  try {
    // Bump a harmless field on the signed account node so the user's account
    // graph itself is definitely re-sent to the relay. Namespaced under
    // `timeTracker` so it cannot collide with Gun's own user-graph keys.
    //
    // ownedRootWritable, not ownedRoot: the whole point is to re-send the SIGNED
    // account graph, and the read path resolves the same soul without signing —
    // so with no live session this wrote an unsigned put that the relay drops
    // and the local store acks, republishing nothing at all.
    await putAck(ownedRootWritable(pub).get('_accountSync'), {
      at: new Date().toISOString(),
      alias: cleanAlias || alias,
      pub,
    });
  } catch (e) {
    console.warn('[gunAuth] account republish sync failed:', e);
  }

  if (!cleanAlias) return;

  try {
    // Reassert the public alias lookup directly. Gun SEA accepts this exact
    // link shape for `~@alias`, and fresh devices need this node to resolve
    // the username during auth.
    //
    // ⚠️ `~@alias` is a SET of account links, and it is NOT access-controlled:
    // any peer can add its own `~<pub>` entry to someone else's alias node.
    // That does not break login (SEA still has to decrypt the account with the
    // password), but it means the node cannot answer "which pub owns this
    // alias" on its own. The authoritative direction is the other one —
    // `~<pub>/alias` lives inside the account's signed graph. Resolve pub →
    // alias, never alias → pub, when the answer has to be trusted
    // (see `aliasBelongsToPub`).
    const pubSoul = `~${pub}`;
    await putAck(gunInstance().get(`~@${cleanAlias}`), {
      [pubSoul]: { '#': pubSoul },
    });
  } catch (e) {
    console.warn('[gunAuth] alias republish failed:', e);
  }
}

/**
 * Boot-time recall. Call ONCE at app startup.
 * Returns {pub, alias} or null.
 */
let bootPromise: Promise<{ pub: string; alias: string | null } | null> | null = null;

// The boot promise is memoized for the lifetime of the module, so an in-session
// logout — or a login as a different account — would otherwise pin the old
// session forever and make a later gunBoot() resolve the wrong user.
onSessionReset(() => {
  bootPromise = null;
});

export function gunBoot(): Promise<{ pub: string; alias: string | null } | null> {
  if (bootPromise) return bootPromise;

  bootPromise = (async () => {
    const u = gunUser();
    const remember = getRememberMe();

    // --- Manual Bridge: Try to restore session from stored SEA pair first ---
    const pair = remember ? await readStoredGunPair() : null;
    debugLog('[gunBoot] GUN_PAIR exists in storage:', !!pair);

    let authSettled: Promise<void> = Promise.resolve();
    if (pair) {
      debugLog('[gunBoot] Found stored pair, attempting direct auth with SEA pair...');
      // Await the ack (bounded below) instead of firing-and-forgetting: a slow
      // auth used to outlive the fixed 2.5s poll, so boot resolved null (the
      // app rendered signed out) while the SEA session materialized moments
      // later — auth state and Gun session inconsistent until reload.
      authSettled = new Promise<void>((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        try {
          u.auth(pair, (ack: any) => {
            if (ack?.err) {
              console.warn('[gunBoot] stored SEA pair rejected:', ack.err);
              // Only discard on a definitive rejection of the pair itself.
              const err = String(ack.err).toLowerCase();
              if (err.includes('pair') || err.includes('decrypt') || err.includes('signature')) {
                storage.remove(STORAGE_KEYS.GUN_PAIR);
                debugLog('[gunBoot] Cleared known-bad stored pair');
              }
            } else {
              debugLog('[gunBoot] Stored pair authenticated. pub:', ack?.pub ?? u?.is?.pub);
            }
            finish();
          }, { remember });
        } catch {
          finish();
        }
        // Gun sometimes never calls the ack; don't hang boot forever.
        setTimeout(finish, 10000);
      });
    } else {
      try {
        // Fallback to Gun's internal recall
        u.recall({ sessionStorage: true }, () => { });
        if (remember) {
          u.recall({ sessionStorage: false }, () => { });
        }
      } catch (e) {
        console.warn("[gunBoot] recall trigger failed:", e);
      }
    }

    // With a stored pair, wait for its auth ack (bounded at 10s) before the
    // poll; without one, the old 2.5s recall poll suffices.
    await authSettled;
    const pub = await gunRecallSession(2500, 100);

    if (!pub) return null;

    const raw = u?.is?.alias ? String(u.is.alias) : null;
    const safeRaw = raw && !looksLikePub(raw) ? raw : null;
    const alias = safeRaw ?? getLastAlias() ?? null;

    if (alias && !looksLikePub(alias)) setLastAlias(alias);
    return { pub, alias };
  })().catch(() => null);

  return bootPromise;
}

export async function gunLogin(
  alias: string,
  pass: string,
  opts?: { remember?: boolean }
): Promise<Fail | { ok: true; pub: string; alias: string }> {
  const u = gunUser();

  debugLog('[gunLogin] === gunLogin called ===');
  debugLog('[gunLogin] Current u.is before auth:', u?.is);
  debugLog('[gunLogin] Already authenticated pub:', u?.is?.pub);
  
  const storedPair = storage.get<string>(STORAGE_KEYS.GUN_PAIR, "");
  const storedActiveUser = storage.get<string>(STORAGE_KEYS.ACTIVE_USER, "");
  const storedLastAlias = storage.get<string>(STORAGE_KEYS.LAST_ALIAS, "");
  debugLog('[gunLogin] Storage - GUN_PAIR exists:', !!storedPair, 'ACTIVE_USER:', storedActiveUser?.slice(0,10) + '...', 'LAST_ALIAS:', storedLastAlias);

  // A previous attempt that timed out leaves SEA's latch set, which would
  // refuse this login (and the session restore below) outright.
  clearStaleAuthLatch(u);

  const cleanAlias = String(alias || "").trim();
  debugLog('[gunLogin] Attempting login for alias:', cleanAlias);

  if (!cleanAlias) {
    debugLog('[gunLogin] ERROR: Username is required');
    return { ok: false, error: "Username is required" };
  }
  if (!pass) {
    debugLog('[gunLogin] ERROR: Password is required');
    return { ok: false, error: "Password is required" };
  }

  // Always force a fresh auth attempt for the login form.
  // This prevents a pre-existing in-memory SEA session from making a login
  // appear successful without actually verifying the entered password.
  //
  // BUT: keep a snapshot of the session we are tearing down. If the new auth
  // fails (wrong password, relay timeout, ...) we restore it, so a mistyped
  // password can no longer log the user out of a perfectly valid session.
  const previousPair: any = u?.is?.pub ? (u as any)._?.sea ?? null : null;
  const previousPub: string | null = u?.is?.pub ? String(u.is.pub) : null;
  const hadSession = !!u?.is?.pub;
  if (hadSession) {
    debugLog('[gunLogin] Clearing existing authenticated session before fresh login attempt');
    try {
      u.leave();
    } catch (e) {
      console.warn('[gunLogin] failed to clear existing session before login:', e);
    }
  }

  // Set by the auth callback the moment Gun answers; checked by the timeout
  // path and the restore below, both of which must stand down once the real
  // ack owns the outcome. Declared here (not in the promise executor) so
  // restorePreviousSession can see it.
  let ackAccepted = false;

  // Re-auth the previous pair after a failed attempt. Best-effort, never throws.
  const restorePreviousSession = async () => {
    if (!hadSession || !previousPair || u?.is?.pub) return;
    // A successful ack means the new session is the current one; re-authing the
    // old pair now would fight the login that just won.
    if (ackAccepted) return;
    debugLog('[gunLogin] Login failed; restoring the previous session');
    // The failed attempt may have stranded SEA's latch. Without clearing it the
    // restore is refused and the user is left logged out by a mistyped password
    // or an unlucky relay — the exact outcome this restore exists to prevent.
    authInFlight = false;
    clearStaleAuthLatch(u);
    // Tracked like any other auth: a user retrying login during this window
    // would otherwise have clearStaleAuthLatch clear SEA's latch mid-genuine
    // auth, which SEA does not expect.
    authInFlight = true;
    try {
      await new Promise<void>((res) => {
        try {
          u.auth(previousPair, () => res(), { remember });
        } catch {
          res();
        }
        setTimeout(res, 3000);
      });
    } finally {
      authInFlight = false;
    }
  };

  const remember = opts?.remember ?? getRememberMe();
  debugLog('[gunLogin] remember:', remember, 'opts?.remember:', opts?.remember);
  setRememberMe(remember);

  // Give the relay a chance to connect, but do not require it. The account's
  // `~@alias` node may already be in the local store, in which case SEA can
  // authenticate with no network at all; refusing to even try turned a
  // momentarily unreachable relay into "you cannot log in". If the account
  // genuinely is not cached, `u.auth` below fails on its own timeout.
  debugLog('[gunLogin] Waiting for peer (best effort)...');
  try {
    await waitForPeer();
    debugLog('[gunLogin] Peer ready, proceeding with auth');
  } catch (peerError) {
    debugLog('[gunLogin] No peer; attempting auth from the local store:', peerError);
  }

  debugLog('[gunLogin] Calling u.auth() with alias:', cleanAlias, 'pass length:', pass.length);
  
  return await new Promise((resolve) => {
    // Gun's auth ack is not guaranteed to arrive: a relay that accepts the
    // socket but never answers leaves this promise pending forever, and the
    // previous session (already leave()d above) is never restored. Bound it.
    let settled = false;
    /**
     * The ack timeout guards ONE risk: Gun never calling back at all. Once the
     * ack is in hand that risk is gone, and leaving the timer armed would abandon
     * a login that had already succeeded while its bounded follow-up work (the
     * account-switch wipe, the alias republish) was still running.
     */
    let ackArrived = false;
    const settleOnce = (result: Fail | { ok: true; pub: string; alias: string }) => {
      if (settled) return;
      settled = true;
      clearTimeout(authTimeout);
      // Whatever happened, this attempt is over. Releasing our own flag lets
      // the next login clear SEA's latch if Gun left it set.
      authInFlight = false;
      resolve(result);
    };

    const authTimeout = setTimeout(() => {
      if (settled || ackArrived) return;
      console.warn('[gunLogin] auth ack timed out after', AUTH_ACK_TIMEOUT_MS, 'ms');
      void (async () => {
        // Abandon the attempt: Gun's flow never completed, so it will not clear
        // its own latch and every later login would be refused outright.
        authInFlight = false;
        clearStaleAuthLatch(u);
        // The real ack can land between the synchronous check above and each
        // await below. Once it has, the ack path owns this login: restoring the
        // previous pair here would issue a second u.auth against a session that
        // just authenticated, and settling would report "timed out" for a login
        // that succeeded.
        if (settled || ackArrived) return;
        await restorePreviousSession();
        if (settled || ackArrived) return;
        settleOnce({ ok: false, error: 'Login timed out. Please try again.' });
      })();
    }, AUTH_ACK_TIMEOUT_MS);

    authInFlight = true;
    u.auth(cleanAlias, pass, async (ack: any) => {
      if (settled) {
        // The timeout already reported this attempt as failed — but SEA has
        // still switched the live session to the NEW account underneath the
        // app. Left alone, the app believes the old account is current while
        // every signature comes from the new one, and no session reset runs —
        // per-account state (replay marks, pairwise secrets) is applied
        // cross-account. Tear the surprise session down and put the previous
        // one back.
        if (!ack?.err && u?.is?.pub && u.is.pub !== previousPub) {
          debugLog('[gunLogin] Late auth ack after timeout; reverting surprise session');
          try { u.leave(); } catch { /* best effort */ }
          runSessionReset();
          await restorePreviousSession();
        }
        return;
      }
      ackArrived = true;
      clearTimeout(authTimeout);
      debugLog('[gunLogin] Auth callback received. ack:', ack);
      debugLog('[gunLogin] Auth callback - u.is:', u?.is);
      
      if (!ack || ack.err) {
        const errMsg = ack?.err ? String(ack.err) : "Login failed";
        debugLog('[gunLogin] Auth FAILED. Error:', errMsg, 'ack.err:', ack?.err);
        await restorePreviousSession();
        settleOnce({ ok: false, error: errMsg });
        return;
      }

      let pub = u?.is?.pub ? String(u.is.pub) : "";
      if (!pub) {
        debugLog('[gunLogin] Auth ack arrived before u.is.pub; waiting briefly for session materialization...');
        pub = await waitForAuthenticatedPub(3000, 80) || "";
      }
      const gotAlias = u?.is?.alias ? String(u.is.alias) : cleanAlias;
      
      debugLog('[gunLogin] Auth SUCCEEDED. pub:', pub, 'gotAlias:', gotAlias, 'u.is:', u?.is);

      if (!pub) {
        debugLog('[gunLogin] ERROR: Login succeeded but no pub key found');
        await restorePreviousSession();
        settleOnce({ ok: false, error: "Login failed (no pub)" });
        return;
      }
      // The new session is authenticated and current: from here on, no path —
      // including a concurrently running timeout handler — may re-auth the old
      // pair underneath it.
      ackAccepted = true;

      // --- A different account is now current: drop per-account state ---
      // This login replaced a live session rather than following a logout, so
      // gunLogout never ran and none of the onSessionReset hooks had fired.
      // Everything memoized from the OLD pub — pairwise SEA secrets, replay
      // high-water marks, sequence allocators — would otherwise be applied to
      // this one. Runs before the post-auth hook so the wipe and the first sync
      // both see clean state.
      if (previousPub && previousPub !== pub) {
        debugLog('[gunLogin] Account switched; running session-reset hooks');
        runSessionReset();
      }

      // --- Whatever the app must do before syncing starts (see setPostAuthHook) ---
      // Here: wipe the previous account's local data on a switch. Awaited,
      // because that wipe deletes the IndexedDB graph out from under the live
      // Gun instance and has to finish before the app starts syncing the new
      // account into that same store.
      debugLog('[gunLogin] Running post-auth hook...');
      try {
        await runPostAuthHook(pub);
      } catch (e) {
        console.error('[gunLogin] post-auth hook failed:', e);
        settleOnce({ ok: false, error: 'Could not prepare this device for the account.' });
        return;
      }

      // --- Manual Bridge: Save the SEA pair ---
      if (remember) {
        try {
          // u._.sea contains the cryptographic pair after auth
          const pair = (u as any)._.sea;
          debugLog('[gunLogin] Saving SEA pair to storage. Pair exists:', !!pair);
          if (pair) {
            // Encrypted at rest — see writeStoredGunPair.
            await writeStoredGunPair(pair);
            debugLog('[gunLogin] SEA pair saved successfully');
          } else {
            debugLog('[gunLogin] WARNING: No SEA pair found in u._.sea');
          }
        } catch (e) {
          console.warn("[gunLogin] failed to save pair:", e);
          debugLog('[gunLogin] ERROR saving pair:', e);
        }
      }

      setLastAlias(gotAlias);
      debugLog('[gunLogin] Last alias set to:', gotAlias);

      // Register device for cross-device tracking
      try {
        registerDevice(pub);
        debugLog('[gunLogin] Device registered:', pub);
      } catch (e) {
        console.warn("[gunLogin] Device registration failed:", e);
        debugLog('[gunLogin] Device registration error:', e);
      }
      
      // Force-publish account to relay so fresh devices can resolve ~@alias
      // during auth. Note this is a LOGIN aid, not a trust anchor: `~@alias` is
      // world-writable (see republishAccountToRelay). An alias claim is only
      // confirmed by reading `~<pub>/alias` back out of the account's signed
      // graph.
      try {
        await republishAccountToRelay(pub, gotAlias);
      } catch (e) {
        console.warn('[gunLogin] account republish error:', e);
      }

      // App-owned publishing (e.g. the search index) — see setLoginPublishHook.
      try {
        void loginPublishHook?.(pub, gotAlias);
      } catch (e) {
        console.warn('[gunLogin] login publish hook failed:', e);
      }


      debugLog('[gunLogin] Resolving with success. pub:', pub, 'alias:', gotAlias);
      settleOnce({ ok: true, pub, alias: gotAlias });
      }, { remember });
  });
}

export async function gunRegisterAndLogin(
  alias: string,
  pass: string,
  opts?: { remember?: boolean }
): Promise<Fail | { ok: true; pub: string; alias: string; created: boolean }> {
  const u = gunUser();
  
  debugLog('[gunRegisterAndLogin] === gunRegisterAndLogin called ===');
  debugLog('[gunRegisterAndLogin] Current u.is before registration:', u?.is);

  const cleanAlias = String(alias || "").trim();
  debugLog('[gunRegisterAndLogin] Attempting registration for alias:', cleanAlias);
  
  if (!cleanAlias) {
    debugLog('[gunRegisterAndLogin] ERROR: Username is required');
    return { ok: false, error: "Username is required" };
  }
  if (!pass) {
    debugLog('[gunRegisterAndLogin] ERROR: Password is required');
    return { ok: false, error: "Password is required" };
  }

  const remember = opts?.remember ?? getRememberMe();
  debugLog('[gunRegisterAndLogin] remember:', remember);
  setRememberMe(remember);

  // Registration genuinely needs the relay: a new account nobody else can see
  // is not an account. A failure here is reported rather than thrown.
  debugLog('[gunRegisterAndLogin] Waiting for peer before registration...');
  try {
    await waitForPeer();
  } catch {
    return {
      ok: false,
      error: 'Cannot reach the sync server, so a new account cannot be created yet. Please try again when you are online.',
    };
  }
  debugLog('[gunRegisterAndLogin] Peer ready, proceeding with registration');

  // Same stranded-latch recovery as login: an earlier abandoned attempt would
  // otherwise refuse this one outright.
  clearStaleAuthLatch(u);

  debugLog('[gunRegisterAndLogin] Calling u.create() for alias:', cleanAlias);
  const created = await new Promise<boolean | Fail>((resolve) => {
    // `u.create` has no ack guarantee either, so bound it the same way as auth.
    let settled = false;
    const settleOnce = (result: boolean | Fail) => {
      if (settled) return;
      settled = true;
      clearTimeout(createTimeout);
      authInFlight = false;
      resolve(result);
    };
    const createTimeout = setTimeout(() => {
      if (settled) return;
      console.warn('[gunRegisterAndLogin] create ack timed out');
      authInFlight = false;
      clearStaleAuthLatch(u);
      settleOnce({ ok: false, error: 'Account creation timed out. Please try again.' });
    }, AUTH_ACK_TIMEOUT_MS);

    authInFlight = true;
    u.create(cleanAlias, pass, (ack: any) => {
      if (settled) return;
      debugLog('[gunRegisterAndLogin] Create callback received. ack:', ack);
      debugLog('[gunRegisterAndLogin] Create callback - u.is after create:', u?.is);
      const err = ack?.err ? String(ack.err) : "";
      if (err) {
        debugLog('[gunRegisterAndLogin] Create ERROR:', err);
        if (err.toLowerCase().includes("already created")) {
          debugLog('[gunRegisterAndLogin] User already exists, will proceed to login');
          settleOnce(false); // not created, proceed to login
          return;
        }
        settleOnce({ ok: false, error: err });
        return;
      }
      debugLog('[gunRegisterAndLogin] User created successfully! ack.pub:', ack?.pub, 'u.is?.pub:', u?.is?.pub);
      // Check if u.is is set after create
      if (u?.is?.pub) {
        debugLog('[gunRegisterAndLogin] u.is is set after create. pub:', u.is.pub, 'alias:', u.is.alias);
      } else {
        debugLog('[gunRegisterAndLogin] WARNING: u.is is NOT set after create!');
      }
      settleOnce(true);
    });
  });

  if (typeof created === "object" && created && (created as any).ok === false) {
    debugLog('[gunRegisterAndLogin] Registration failed with error:', (created as Fail).error);
    return created as Fail;
  }

  debugLog('[gunRegisterAndLogin] Now attempting login for:', cleanAlias);
  debugLog('[gunRegisterAndLogin] State before login - u.is:', u?.is);
  const loggedIn = await gunLogin(cleanAlias, pass, { remember });
  
  if (!loggedIn.ok) {
    debugLog('[gunRegisterAndLogin] Login after registration FAILED:', loggedIn.error);
    return loggedIn;
  }

  debugLog('[gunRegisterAndLogin] Registration and login successful! pub:', loggedIn.pub);
  return { ok: true, pub: loggedIn.pub, alias: loggedIn.alias, created: Boolean(created) };
}
